'use client';

import { useEffect, useRef, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import { isClimaxNodeData } from './attack-path-reveal-theater.util';

const EMPTY_IDS: ReadonlySet<string> = new Set();

export const useAttackPathRevealBeats = ({
  contentHiddenById,
  frameHiddenById,
  revealFingerprint,
  isRevealing,
  sourceNodes,
  sourceEdges,
  onClimaxReveal,
}: {
  contentHiddenById: ReadonlyMap<string, boolean>;
  frameHiddenById: ReadonlyMap<string, boolean>;
  revealFingerprint: string;
  isRevealing: boolean;
  sourceNodes: Node<AttackPathEventNodeData>[];
  sourceEdges: Edge<AttackPathEventEdgeData>[];
  onClimaxReveal?: () => void;
}) => {
  const previousHiddenIdsRef = useRef<Set<string> | null>(null);
  const previousFrameHiddenRef = useRef<Map<string, boolean> | null>(null);
  const wasRevealingRef = useRef(false);
  const timersRef = useRef<number[]>([]);
  const [climaxNodeIds, setClimaxNodeIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);
  const [drawingEdgeIds, setDrawingEdgeIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);
  const [pathInkEdgeIds, setPathInkEdgeIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);

  useEffect(() => {
    return () => {
      for (const timer of timersRef.current) {
        window.clearTimeout(timer);
      }

      timersRef.current = [];
    };
  }, []);

  useEffect(() => {
    const hiddenIds = new Set(
      [...contentHiddenById.entries()]
        .filter(([, hidden]) => hidden)
        .map(([id]) => id),
    );

    if (!isRevealing) {
      for (const timer of timersRef.current) {
        window.clearTimeout(timer);
      }

      timersRef.current = [];
      previousHiddenIdsRef.current = hiddenIds;
      previousFrameHiddenRef.current = new Map(frameHiddenById);
      wasRevealingRef.current = false;
      setClimaxNodeIds(EMPTY_IDS);
      setDrawingEdgeIds(EMPTY_IDS);
      setPathInkEdgeIds(EMPTY_IDS);

      return;
    }

    if (
      !wasRevealingRef.current ||
      previousHiddenIdsRef.current === null ||
      previousFrameHiddenRef.current === null
    ) {
      previousHiddenIdsRef.current = hiddenIds;
      previousFrameHiddenRef.current = new Map(frameHiddenById);
      wasRevealingRef.current = true;

      return;
    }

    const newlyVisible = [...contentHiddenById.entries()]
      .filter(
        ([id, hidden]) => !hidden && previousHiddenIdsRef.current?.has(id),
      )
      .map(([id]) => id);

    previousHiddenIdsRef.current = hiddenIds;
    previousFrameHiddenRef.current = new Map(frameHiddenById);
    wasRevealingRef.current = true;

    if (newlyVisible.length === 0) {
      return;
    }

    const climaxIds = newlyVisible.filter((id) => {
      const node = sourceNodes.find((candidate) => candidate.id === id);

      return node ? isClimaxNodeData(node.data) : false;
    });
    const edgeIds = sourceEdges
      .filter(
        (edge) =>
          newlyVisible.includes(edge.target) ||
          newlyVisible.includes(edge.source),
      )
      .map((edge) => edge.id);

    if (climaxIds.length > 0) {
      onClimaxReveal?.();
      setClimaxNodeIds((current) => new Set([...current, ...climaxIds]));
      timersRef.current.push(
        window.setTimeout(() => {
          setClimaxNodeIds((current) => {
            const next = new Set(current);

            for (const id of climaxIds) {
              next.delete(id);
            }

            return next.size === 0 ? EMPTY_IDS : next;
          });
        }, 900),
      );
    }

    if (edgeIds.length > 0) {
      setDrawingEdgeIds((current) => new Set([...current, ...edgeIds]));
      setPathInkEdgeIds((current) => new Set([...current, ...edgeIds]));
      timersRef.current.push(
        window.setTimeout(() => {
          setDrawingEdgeIds((current) => {
            const next = new Set(current);

            for (const id of edgeIds) {
              next.delete(id);
            }

            return next.size === 0 ? EMPTY_IDS : next;
          });
        }, 500),
      );
      timersRef.current.push(
        window.setTimeout(() => {
          setPathInkEdgeIds((current) => {
            const next = new Set(current);

            for (const id of edgeIds) {
              next.delete(id);
            }

            return next.size === 0 ? EMPTY_IDS : next;
          });
        }, 450),
      );
    }
  }, [
    contentHiddenById,
    frameHiddenById,
    isRevealing,
    onClimaxReveal,
    revealFingerprint,
    sourceEdges,
    sourceNodes,
  ]);

  return {
    climaxNodeIds,
    drawingEdgeIds,
    pathInkEdgeIds,
    bootFrameIds: EMPTY_IDS,
  };
};
